import React from 'react';
import { useController } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { Listbox } from '@headlessui/react';
import { LuCheck, LuChevronsUpDown } from 'react-icons/lu';

type Props = {
  id: string;
  onChange?: (e: any) => void;
  options: any[] | undefined;
  index?: string;
  placeholder: string;
  required?: boolean;
  multiple?: boolean;
  unactive?: boolean;
  label?: string;
};

export const Select = ({
  id,
  onChange,
  options,
  index,
  placeholder,
  required = false,
  multiple = false,
  unactive = false,
  label,
}: Props): any => {
  const {
    field,
    fieldState: { error },
  } = useController({ name: id });

  const handleChange = (value: any) => {
    if (onChange) {
      onChange({ target: { name: id, value } });
    }
    field.onChange(value);
  };

  return (
    <Listbox
      {...field}
      as="div"
      by={index}
      value={field.value || ''}
      onChange={handleChange}
      multiple={multiple}
    >
      {({ open, value }) => (
        <>
          <Label $error={!!error}>
            {label}
            {label && required && <span>*</span>}
          </Label>
          <Button $error={!!error} $disabled={unactive}>
            <SelectedItem
              $isEmpty={
                multiple ? value.length <= 0 : index ? !value[index] : !value
              }
            >
              {(multiple ? value.length <= 0 : index ? !value[index] : !value)
                ? placeholder
                : multiple
                ? value.map((v: any) => (index ? v[index] : v)).join(', ')
                : index
                ? value[index]
                : value}
            </SelectedItem>

            <MenuIcon>
              <LuChevronsUpDown />
            </MenuIcon>
          </Button>

          <Options static $isOpen={open}>
            {!multiple && !required && <Option value="">{placeholder}</Option>}
            {options?.map((option: any, idx: number) => (
              <Option key={idx} value={option} disabled={unactive}>
                {({ selected }) => (
                  <>
                    <ItemText $selected={selected}>
                      {index ? option[index] : option}
                    </ItemText>
                    {selected ? (
                      <ItemIcon>
                        <LuCheck />
                      </ItemIcon>
                    ) : null}
                  </>
                )}
              </Option>
            ))}
          </Options>
        </>
      )}
    </Listbox>
  );
};

const Button = styled(Listbox.Button)<{ $error: boolean; $disabled: boolean }>`
  position: relative;
  width: 100%;
  cursor: pointer;
  border-radius: 0.5rem;
  background-color: white;
  font-weight: 500;
  box-sizing: border-box;
  padding: 0.7rem 2rem 0.7rem 0.75rem;
  font-size: 0.9rem;
  text-align: left;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  border: 0.1rem solid ${({ $error }) => ($error ? 'red' : '#d1d5db')};
  &[aria-expanded='true'] {
    outline: none;
    border-bottom: 0.2rem solid ${({ $error }) => ($error ? 'red' : '#154077')};
  }
  ${({ $disabled }) =>
    $disabled &&
    css`
      box-shadow: none;
      background-color: #d2d7d3;
      opacity: 0.9;
      cursor: default;
    `}
`;

const Label = styled(Listbox.Label)<{ $error: boolean }>`
  display: block;
  line-height: 1.25rem;
  font-weight: 600;
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $error }) => ($error ? 'red' : '#154077')};
  margin-bottom: 0.5rem;
  & > span {
    margin-left: 0.2rem;
  }
`;

const Options = styled(Listbox.Options)<{ $isOpen: boolean }>`
  position: absolute;
  margin-top: 0.25rem;
  max-height: 15rem;
  max-width: 80%;
  overflow-y: auto;
  border-radius: 0.375rem;
  background-color: white;
  padding-top: 0.25rem;
  font-size: 0.9rem;
  line-height: 1.5rem;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  transform: ${({ $isOpen }) => `${$isOpen ? 'scale(1)' : 'scale(0.95)'}`};
  transition: all 0.15s ease-in-out;
  z-index: 2;
`;

const Option = styled(Listbox.Option)`
  position: relative;
  cursor: pointer;
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  color: rgb(17, 24, 39);
  border-left: 3px solid transparent;

  &:hover {
    border-left: 3px solid #154077;
    background-color: #ecfccb;
    & > span {
      color: #154077;
    }
  }
`;

const SelectedItem = styled.span<{ $isEmpty: boolean }>`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $isEmpty }) => ($isEmpty ? '#9ca3af' : 'black')};
`;

const ItemText = styled.span<{ $selected: boolean }>`
  display: block;
  overflow: auto;
  white-space: nowrap;
  font-weight: ${({ $selected }) => ($selected ? '500' : '400')};
`;

const MenuIcon = styled.span`
  position: absolute;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  top: 0;
  bottom: 0;
  right: 0;
  padding-right: 0.5rem;
  & > svg {
    color: #9ca3af;
    height: 1.25rem;
    width: 1.25rem;
  }
`;

const ItemIcon = styled.span`
  position: absolute;
  display: flex;
  align-items: center;
  padding-left: 0.75rem;
  color: #154077;
  top: 0;
  bottom: 0;
  left: 0;

  & > svg {
    stroke-width: 3;
    height: 1.25rem;
    width: 1.25rem;
  }
`;
